import {
  CollectionConfig,
  CollectionHeader,
  CollectionTileConfig,
  CollectionType,
} from './types'
import { collectionAssets } from './collectionAssets'

type CollectionConfigWithoutImages = Omit<
  CollectionConfig,
  'header' | 'tile'
> & {
  header: Omit<CollectionHeader, 'image'>
  tile: Omit<CollectionTileConfig, 'image'>
}

const withCollectionAssets = (
  type: CollectionType,
  config: CollectionConfigWithoutImages,
): CollectionConfig => ({
  ...config,
  header: { ...config.header, image: collectionAssets[type].header },
  tile: { ...config.tile, image: collectionAssets[type].thumbnail },
})

export const Collections: Record<CollectionType, CollectionConfig> = {
  [CollectionType.DisneyFan]: withCollectionAssets(CollectionType.DisneyFan, {
    promotionId: '9001',
    missionId: 'occasion:occasion-birthday',
    mission: 'birthday',
    header: {
      title: 'Disney fan',
      description:
        'Bring a little Disney sparkle to their day with cards, gifts and flowers that turn every moment into happily ever after.',
    },
    tile: {
      title: 'Disney fan',
      path: '/uk/collections/disney-fan',
      backgroundColor: '#F9E2E2',
    },
  }),
  [CollectionType.TattyTeddyCollector]: withCollectionAssets(
    CollectionType.TattyTeddyCollector,
    {
      promotionId: '9002',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Tatty Teddy collector',
        description:
          'Packed with cuddly charm, our Tatty Teddy cards, gifts and flowers are made for sharing the loveliest kind of love.',
      },
      tile: {
        title: 'Tatty Teddy collector',
        path: '/uk/collections/tatty-teddy-collector',
        backgroundColor: '#E0E2F4',
      },
    },
  ),
  [CollectionType.CathKidstonFan]: withCollectionAssets(
    CollectionType.CathKidstonFan,
    {
      promotionId: '9003',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Cath Kidston fan',
        description:
          'Perfect for adding a pop of print to their day, our Cath Kidston cards, gifts and flowers are made for feel-good moments.',
      },
      tile: {
        title: 'Cath Kidston fan',
        path: '/uk/collections/cath-kidston-fan',
        backgroundColor: '#F7E3E7',
      },
    },
  ),
  [CollectionType.ChocolateLover]: withCollectionAssets(
    CollectionType.ChocolateLover,
    {
      promotionId: '9004',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Chocolate lover',
        description:
          'Perfect for treating your favourite choc addict, these gifts are packed with rich, indulgent bites made to delight them from the very first nibble.',
      },
      tile: {
        title: 'Chocolate lover',
        path: '/uk/collections/chocolate-lover',
        backgroundColor: '#FCE8DD',
      },
    },
  ),
  [CollectionType.FootballFan]: withCollectionAssets(
    CollectionType.FootballFan,
    {
      promotionId: '9005',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Football fan',
        description:
          'Kick off the fun with cards and gifts made for big fans, packed with winning moments they’ll cheer about long after full-time.',
      },
      tile: {
        title: 'Football fan',
        path: '/uk/collections/football-fan',
        backgroundColor: '#E0F4EA',
      },
    },
  ),
  [CollectionType.ProudGardener]: withCollectionAssets(
    CollectionType.ProudGardener,
    {
      promotionId: '9006',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Proud gardener',
        description:
          'Dig into personal cards and gifts made for green fingers, full of blooming lovely touches they’ll adore.',
      },
      tile: {
        title: 'Proud gardener',
        path: '/uk/collections/proud-gardener',
        backgroundColor: '#E0F1E0',
      },
    },
  ),
  [CollectionType.Gamer]: withCollectionAssets(CollectionType.Gamer, {
    promotionId: '9007',
    missionId: 'occasion:occasion-birthday',
    mission: 'birthday',
    header: {
      title: 'Gamer',
      description:
        'For those who’d rather be gaming, level up their day with these cards and gifts that bring all the fun.',
    },
    tile: {
      title: 'Gamer',
      path: '/uk/collections/gamer',
      backgroundColor: '#E0E2F4',
    },
  }),
  [CollectionType.PetParent]: withCollectionAssets(CollectionType.PetParent, {
    promotionId: '9008',
    missionId: 'occasion:occasion-birthday',
    mission: 'birthday',
    header: {
      title: 'Pet parent',
      description:
        'Packed with paw-some charm, these cards and gifts are perfect for anyone who’s happiest with a furry friend by their side.',
    },
    tile: {
      title: 'Pet parent',
      path: '/uk/collections/pet-parent',
      backgroundColor: '#DDEFF1',
    },
  }),
  [CollectionType.WellnessObsessed]: withCollectionAssets(
    CollectionType.WellnessObsessed,
    {
      promotionId: '9009',
      missionId: 'occasion:occasion-birthday',
      mission: 'birthday',
      header: {
        title: 'Wellness obsessed',
        description:
          'For those who love a good pamper, these cards and gifts are packed with feel-good moments and well-earned indulgence.',
      },
      tile: {
        title: 'Wellness obsessed',
        path: '/uk/collections/wellness-obsessed',
        backgroundColor: '#E0EEED',
      },
    },
  ),
}
